import { supabaseGet, supabasePost, supabaseDelete, supabasePatch } from "@/utils/supabaseRest";

/**
 * Flow template cloning service — uses raw fetch (not supabase client).
 */

export interface FlowTemplateRow {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  category: string | null;
  verticals: string[] | null;
  estimated_rpus: number | null;
  popularity: number | null;
  nodes: TemplateNode[];
  start_node_slug: string;
  is_public: boolean;
}

interface TemplateNode {
  slug: string;
  type: string;
  field?: string;
  question?: string;
  input_type?: string;
  options?: unknown;
  validation?: unknown;
  position?: number;
  next_slug?: string | null;
  rules?: TemplateRule[];
  meta?: Record<string, unknown> | null;
}

interface TemplateRule {
  field?: string;
  operator?: string;
  value?: unknown;
  next_node_slug?: string | null;
  [k: string]: unknown;
}

export async function cloneTemplate(
  templateId: string,
  userId: string
): Promise<{ chatbotId: string }> {
  if (!templateId) throw new Error("Template id is required");
  if (!userId) throw new Error("You must be signed in to use a template");

  // 1. Load the template
  const templates = await supabaseGet<FlowTemplateRow[]>(
    `flow_templates?id=eq.${templateId}&is_public=eq.true`
  );
  const tpl = templates[0];
  if (!tpl) throw new Error("Template not found or is not public");

  const templateNodes = Array.isArray(tpl.nodes) ? tpl.nodes : [];
  if (templateNodes.length === 0) throw new Error("Template has no nodes");

  // 2. Create chatbot
  const chatbotRows = await supabasePost<Array<{ id: string }>>("chatbots", {
    user_id: userId,
    name: tpl.name,
    description: tpl.description,
  });
  const chatbotId = (Array.isArray(chatbotRows) ? chatbotRows[0] : chatbotRows)?.id;
  if (!chatbotId) throw new Error("Failed to create chatbot from template");

  // 3. Mint UUIDs
  const slugToUuid: Record<string, string> = {};
  for (const node of templateNodes) {
    if (!node.slug) continue;
    slugToUuid[node.slug] = crypto.randomUUID();
  }

  // 4. Rewrite nodes
  const rewrittenNodes = templateNodes.map((node, idx) => {
    const id = slugToUuid[node.slug];
    const next: string[] = [];
    if (node.next_slug && slugToUuid[node.next_slug]) {
      next.push(slugToUuid[node.next_slug]);
    }

    const rules = Array.isArray(node.rules)
      ? node.rules.map((rule) => {
          const { next_node_slug, ...rest } = rule;
          return {
            ...rest,
            next_node: next_node_slug && slugToUuid[next_node_slug] ? slugToUuid[next_node_slug] : null,
          };
        })
      : [];

    let meta: Record<string, unknown> | null = null;
    if (node.meta && typeof node.meta === "object") {
      const { pivot_on_reject_slug, ...restMeta } = node.meta as Record<string, unknown>;
      meta = { ...restMeta };
      if (typeof pivot_on_reject_slug === "string" && slugToUuid[pivot_on_reject_slug]) {
        meta.pivot_on_reject = slugToUuid[pivot_on_reject_slug];
      }
    }

    return {
      id,
      chatbot_id: chatbotId,
      type: node.type,
      field: node.field ?? null,
      question: node.question ?? null,
      input_type: node.input_type ?? null,
      options: node.options ?? [],
      validation: node.validation ?? null,
      rules,
      next,
      meta,
      position: typeof node.position === "number" ? node.position : idx,
    };
  });

  // 5. Insert nodes
  try {
    await supabasePost("flow_nodes", rewrittenNodes);
  } catch (err: any) {
    await supabaseDelete(`chatbots?id=eq.${chatbotId}`).catch(() => {});
    throw new Error(`Failed to clone template nodes: ${err.message}`);
  }

  // 6. Create flow definition
  const startNodeId = slugToUuid[tpl.start_node_slug];
  if (!startNodeId) {
    await supabaseDelete(`flow_nodes?chatbot_id=eq.${chatbotId}`).catch(() => {});
    await supabaseDelete(`chatbots?id=eq.${chatbotId}`).catch(() => {});
    throw new Error(`Template start_node_slug "${tpl.start_node_slug}" did not match any node`);
  }

  try {
    await supabasePost("flow_definitions", {
      chatbot_id: chatbotId,
      name: "Main Flow",
      start_node: startNodeId,
    });
  } catch (err: any) {
    await supabaseDelete(`flow_nodes?chatbot_id=eq.${chatbotId}`).catch(() => {});
    await supabaseDelete(`chatbots?id=eq.${chatbotId}`).catch(() => {});
    throw new Error(`Failed to create flow definition: ${err.message}`);
  }

  // 7. Fire-and-forget popularity bump
  supabasePatch(`flow_templates?id=eq.${templateId}`, {
    popularity: (tpl.popularity ?? 0) + 1,
  }).catch(() => {});

  return { chatbotId };
}
